import React from 'react';
import { useTranslation } from 'react-i18next';

/**
 * Modal showing the full details of a data_import row, including warnings + errors JSON.
 */
const Row = ({ label, value }) => (
  <div className="flex justify-between items-center py-2 border-b border-border last:border-0">
    <div className="text-xs uppercase tracking-widest text-muted-foreground">{label}</div>
    <div className="text-sm font-medium tabular-nums">{value ?? '—'}</div>
  </div>
);

const ImportDetailModal = ({ row, onClose }) => {
  const { t } = useTranslation();
  if (!row) return null;

  return (
    <div
      className="fixed inset-0 z-50 bg-foreground/40 flex items-center justify-center px-4 py-6"
      onClick={onClose}
    >
      <div
        className="bg-card rounded-2xl max-w-2xl w-full max-h-full overflow-auto border border-border shadow-elevation-3"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sticky top-0 bg-card border-b border-border px-6 py-4 flex items-center justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground">
              {t('tcn.imports.detail.title', 'Import detail')}
            </div>
            <div className="font-heading text-lg font-bold">{row.file_name}</div>
          </div>
          <button
            onClick={onClose}
            className="text-muted-foreground hover:text-foreground"
          >✕</button>
        </div>
        <div className="px-6 py-4 space-y-1">
          <Row label="Date" value={row.created_at ? new Date(row.created_at).toLocaleString() : '—'} />
          <Row label="Status" value={row.status} />
          <Row label="Language" value={row.detected_language} />
          <Row label="Mode" value={row.mode} />
          <Row label="Period" value={<span dir="ltr">{`${row.data_period_start ?? '—'} → ${row.data_period_end ?? '—'}`}</span>} />
          <Row label="File size" value={row.file_size_bytes ? `${(row.file_size_bytes / 1024).toFixed(1)} KB` : '—'} />
          <Row label="SHA-256" value={<span className="font-mono text-xs break-all">{row.file_hash_sha256}</span>} />
          <Row label="Rows total" value={row.rows_total} />
          <Row label="Creators created" value={row.rows_creators_created} />
          <Row label="Creators upserted" value={row.rows_creators_upserted} />
          <Row label="Managers created" value={row.rows_managers_created} />
          <Row label="Managers upserted" value={row.rows_managers_upserted} />
          <Row label="Metrics rows" value={row.rows_metrics_inserted} />
          <Row label="Marked departed" value={row.rows_marked_departed} />
        </div>
        {row.warnings_log_json && (
          <div className="px-6 pb-4">
            <div className="text-xs uppercase tracking-widest text-muted-foreground mb-1">{t('tcn.imports.detail.warnings', 'Warnings')}</div>
            <pre className="text-[11px] bg-muted/40 rounded-lg p-3 max-h-44 overflow-auto whitespace-pre-wrap">
              {JSON.stringify(row.warnings_log_json, null, 2)}
            </pre>
          </div>
        )}
        {row.error_log_json && (
          <div className="px-6 pb-6">
            <div className="text-xs uppercase tracking-widest text-error mb-1">{t('tcn.imports.detail.errors', 'Errors')}</div>
            <pre className="text-[11px] bg-error/10 rounded-lg p-3 max-h-44 overflow-auto whitespace-pre-wrap">
              {JSON.stringify(row.error_log_json, null, 2)}
            </pre>
          </div>
        )}
      </div>
    </div>
  );
};

export default ImportDetailModal;
